import { useRef, useCallback } from 'react';
import { motion, useInView } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { sendAnalytics } from '@playcastdotio/Analytics';
import { getPersistentPlayerId } from '../../../api';
import { authStore } from '../../../api/stores/globalStores';
import { getUTMParams } from '../../../features/quickstart/utils/utmCapture';
import '../../../styles/aida-base.css';
import '../../../styles/aida-responsive.css';

function buildSteamCardUrl(): string {
  const utms = getUTMParams();
  const params = new URLSearchParams();
  if (utms.utm_source) params.set('utm_source', utms.utm_source);
  else params.set('utm_source', 'playcastlanding');
  if (utms.utm_medium) params.set('utm_medium', utms.utm_medium);
  if (utms.utm_campaign) params.set('utm_campaign', utms.utm_campaign);
  if (utms.utm_term) params.set('utm_term', utms.utm_term);
  if (utms.utm_content) params.set('utm_content', utms.utm_content);
  return `/host/steam-card?${params.toString()}`;
}

/**
 * FinalCTA Component
 * Full-width CTA section with background image, decorative flame streaks,
 * character images, centered headline "PLAY IT FORWARD.", description, and button.
 * Desktop: Absolute positioned layout within 1024px scaler
 * Mobile: Stubbed for future implementation
 */

const WHITE = 'rgba(255, 255, 255, 1)';

const BG_IMAGE =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5769455/image-c5f1f89c-ca0e-4c47-ab3c-c9df7f791be9.png?e=webp&nll=true&cX=87&cY=90&cW=1746&cH=900';

const FLAME_IMAGE = '/img/aida/FinalCTA/FINALCTA_tentacle.png';

const CHARACTER_RIGHT =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5769455/image-b218afc8-c372-4055-91c6-d4e19a6bbfb3.png?w=475&e=webp&nll=true&cX=688&cY=416&cW=608&cH=664';

const CHARACTER_LEFT =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5769455/image-b218afc8-c372-4055-91c6-d4e19a6bbfb3.png?w=616&e=webp&nll=true&cX=516&cY=368&cW=788&cH=712';

interface FlameDecoration {
  left: number;
  top: number;
  width: number;
  height: number;
  rotate: number;
  scaleX?: number;
  scaleY?: number;
  scale?: number;
}

const FLAMES: FlameDecoration[] = [
  { left: 720, top: -220, width: 296, height: 623, rotate: -70, scaleX: -1, scale: 0.89 },
  { left: -80, top: -220, width: 296, height: 623, rotate: 267, scaleX: -1, scaleY: -1 },
  { left: -60, top: 160, width: 296, height: 623, rotate: 126, scaleX: -1, scaleY: -1, scale: 0.89 },
  { left: 610, top: 210, width: 296, height: 623, rotate: 76, scale: 0.85 },
];

const buildFlameTransform = (f: FlameDecoration): string => {
  const parts: string[] = [];
  if (f.rotate !== undefined) parts.push(`rotate(${f.rotate}deg)`);
  if (f.scaleX !== undefined) parts.push(`scaleX(${f.scaleX})`);
  if (f.scaleY !== undefined) parts.push(`scaleY(${f.scaleY})`);
  if (f.scale !== undefined) parts.push(`scale(${f.scale})`);
  return parts.join(' ');
};

export const FinalCTA = () => {
  const ref = useRef(null);
  const mobileRef = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const isMobileInView = useInView(mobileRef, { once: true, amount: 0.1 });
  const navigate = useNavigate();
  const location = useLocation();

  const goToSteamCard = useCallback(() => { sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: 'Join the Network', buttonLocation: 'FinalCTA' }); if (location.pathname === '/hosts') { window.scrollTo({ top: 0, behavior: 'smooth' }); } else { navigate('/hosts'); } }, [navigate, location.pathname]);

  return (
    <>
      {/* Desktop Layout */}
      <motion.section
        ref={ref}
        className="relative mx-auto hidden w-full lg:block"
        style={{
          width: '100%',
          height: '530px',
          backgroundColor: '#000000',
          overflow: 'hidden',
          position: 'relative',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <motion.div
          style={{ position: 'relative', width: '100%', height: '100%' }}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          {/* Top Fade Gradient — black to transparent */}
          <div
            style={{
              position: 'absolute',
              left: '-2px',
              top: '0px',
              width: '1030px',
              height: '193px',
              background:
                'linear-gradient(180deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0.85) 40%, rgba(0,0,0,0.5) 65%, rgba(0,0,0,0) 100%)',
              zIndex: 3,
            }}
          />

          {/* Background Image */}
          <img
            src={BG_IMAGE}
            alt=""
            style={{
              position: 'absolute',
              left: '0px',
              top: '0px',
              width: '1028px',
              height: '530px',
              objectFit: 'cover',
              opacity: 0.56,
              zIndex: 1,
            }}
          />

          {/* Decorative Flame Streaks */}
          {FLAMES.map((flame, i) => (
            <img
              key={i}
              src={FLAME_IMAGE}
              alt=""
              style={{
                position: 'absolute',
                left: `${flame.left}px`,
                top: `${flame.top}px`,
                width: `${flame.width}px`,
                height: `${flame.height}px`,
                transform: buildFlameTransform(flame),
                opacity: 0.44,
                zIndex: 2,
              }}
            />
          ))}

          {/* Character Image — Bottom Right */}
          <img
            src={CHARACTER_RIGHT}
            alt=""
            style={{
              position: 'absolute',
              left: '704px',
              top: '298px',
              width: '152px',
              height: '166px',
              transform: 'rotate(-90deg)',
              zIndex: 4,
            }}
          />

          {/* Character Image — Bottom Left */}
          <img
            src={CHARACTER_LEFT}
            alt=""
            style={{
              position: 'absolute',
              left: '157px',
              top: '313px',
              width: '197px',
              height: '178px',
              zIndex: 4,
            }}
          />

          {/* Title: PLAY IT FORWARD. */}
          <div
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              top: '167px',
              width: '340px',
              zIndex: 5,
              textAlign: 'center',
            }}
          >
            <p
              style={{
                lineHeight: '60px',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 900,
                fontSize: '58px',
                color: WHITE,
                margin: 0,
                fontVariationSettings: "'wght' 900",
              }}
            >
              PLAY IT FORWARD.
            </p>
          </div>

          {/* Description */}
          <div
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              top: '297px',
              width: '342px',
              zIndex: 5,
              textAlign: 'center',
            }}
          >
            <p
              style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                fontSize: '12px',
                color: WHITE,
                margin: 0,
                lineHeight: '1.5',
              }}
            >
              Join thousands of PCs powering the future of gaming. Enable
              others to play games — any place, any time.
            </p>
          </div>

          {/* Join the Network Button */}
          <button
            className="aida-button"
            onClick={goToSteamCard}
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              top: '355px',
              width: '166px',
              height: '30px',
              borderRadius: '30px',
              zIndex: 5,
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: '12px',
              lineHeight: '30px',
              cursor: 'pointer',
            }}
          >
            <span
              className="text"
              style={{ height: '30px', lineHeight: '30px' }}
            >
              Join the Network
            </span>
          </button>
        </motion.div>
      </motion.section>

      {/* Mobile Layout */}
      <motion.section
        ref={mobileRef}
        className="relative flex w-full flex-col items-center lg:hidden"
        style={{
          width: '100%',
          height: '340px',
          backgroundColor: '#000000',
          overflow: 'hidden',
          position: 'relative',
          justifyContent: 'center',
        }}
        initial={{ opacity: 0 }}
        animate={isMobileInView ? { opacity: 1, y: 0 } : { opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Background Image — centered, covers section */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${BG_IMAGE})`,
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            opacity: 0.56,
            zIndex: 1,
          }}
        />

        {/* Top Fade Gradient */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '150px',
            background:
              'linear-gradient(180deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0.85) 40%, rgba(0,0,0,0.5) 65%, rgba(0,0,0,0) 100%)',
            zIndex: 3,
          }}
        />

        {/* Tentacle — left */}
        <img
          src={FLAME_IMAGE}
          alt=""
          style={{
            position: 'absolute',
            left: '160px',
            top: '10px',
            width: '333px',
            height: '700px',
            transform: 'rotate(14deg) scaleX(-1)',
            opacity: 0.44,
            zIndex: 2,
          }}
        />

        {/* Tentacle — right */}
        <img
          src={FLAME_IMAGE}
          alt=""
          style={{
            position: 'absolute',
            left: '-220px',
            top: '-220px',
            width: '333px',
            height: '700px',
            transform: 'rotate(-18deg) scaleX(-1) scaleY(-1)',
            opacity: 0.23,
            zIndex: 2,
          }}
        />

        {/* Content — centered in flow */}
        <div
          style={{
            position: 'relative',
            zIndex: 5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            padding: '0 20px 40px',
          }}
        >
          {/* Title */}
          <p
            style={{
              lineHeight: '42px',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 900,
              fontSize: '40px',
              color: WHITE,
              margin: 0,
              fontVariationSettings: "'wght' 900",
            }}
          >
            PLAY IT<br />FORWARD.
          </p>

          {/* Description */}
          <p
            style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: '16px',
              color: WHITE,
              margin: 0,
              marginTop: '18px',
              lineHeight: '1.5',
              maxWidth: '239px',
            }}
          >
            Join thousands of PCs powering the future of gaming. Enable
            others to play games — any place, any time.
          </p>

          {/* Button */}
          <button
            className="aida-button"
            onClick={goToSteamCard}
            style={{
              width: '207px',
              height: '35px',
              borderRadius: '44px',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: '18px',
              lineHeight: '35px',
              cursor: 'pointer',
              marginTop: '20px',
            }}
          >
            <span
              className="text"
              style={{ height: '35px', lineHeight: '35px' }}
            >
              Join the Network
            </span>
          </button>
        </div>
      </motion.section>
    </>
  );
};
